/**
 * Пульт диспетчера: снапшот, следующий контакт, метка, намерение позвонить.
 *
 * Снапшот приходит по REST и несёт seq журнала событий: дальше пульт
 * применяет дельты из WebSocket, а при разрыве добирает пропущенное по seq (§8.1).
 * Снапшот всегда включает назначения в статусе needs_label — иначе состоявшийся
 * разговор останется без результата.
 */
import { z } from 'zod';
import { assignmentStatusSchema, bigIdSchema, blockedReasonSchema, businessDateSchema, isoDateTimeSchema, phoneE164Schema, refIdSchema, } from './common.dto.js';
/** Карточка контакта в пульте: только то, что диспетчер видит на экране */
export const deskAssignmentSchema = z.object({
    id: bigIdSchema,
    contactId: bigIdSchema,
    queueItemId: bigIdSchema,
    phone: phoneE164Schema,
    fullName: z.string().nullable(),
    regionCode: z.string().nullable(),
    /**
     * Часовой пояс абонента в минутах от UTC; null — реестр региона не знает.
     * Диспетчеру нужно видеть, который час у человека на том конце: «звоню в
     * обед» и «звоню в семь утра» — разные разговоры.
     */
    tzOffsetMin: z.number().int().nullable(),
    /** Произвольные поля от кабинета закупки: показываются как есть */
    extra: z.record(z.string(), z.unknown()),
    sourceId: refIdSchema,
    sourceTitle: z.string(),
    /** 1 — свежий контакт, больше — НДЗ, закреплённый за этим диспетчером */
    roundNumber: z.number().int().min(1).max(20),
    status: assignmentStatusSchema,
    assignedAt: isoDateTimeSchema,
    callsCnt: z.number().int().min(0),
    /** Разговоры, дошедшие до диспетчера: правило «сначала метка» смотрит на них */
    talkedCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    isReached: z.boolean(),
    /**
     * Конец последнего разговора (28.09.2026): пульт ставит наверх самую свежую
     * карточку, где разговор был, а метки нет. null — разговоров не было;
     * поля нет — старый api в окне деплоя, пульт берёт момент выдачи.
     */
    lastTalkAt: isoDateTimeSchema.nullable().optional(),
    /** Оператор номера из реестра диапазонов («что за телефон»); null — неизвестен */
    carrier: z.string().nullable(),
    /**
     * Прошлое закрытие этого же элемента очереди: метка, комментарий и время,
     * на которое договорились. НДЗ и перезвон без «о чём договорились в прошлый
     * раз» заставляют диспетчера звонить вслепую. null — карточка звонится впервые.
     */
    lastResult: z
        .object({
        labelTitle: z.string(),
        comment: z.string().nullable(),
        nextActionAt: isoDateTimeSchema.nullable(),
        labeledAt: isoDateTimeSchema,
    })
        .nullable(),
});
/** Цифры дня самого диспетчера: считаются по первичке, не по витрине */
export const deskDailyStatsSchema = z.object({
    businessDate: businessDateSchema,
    assignedCnt: z.number().int().min(0),
    labeledCnt: z.number().int().min(0),
    callsCnt: z.number().int().min(0),
    uniqueCalledCnt: z.number().int().min(0).default(0),
    uniqueContactCnt: z.number().int().min(0).default(0),
    /** Сняли трубку технически — включая автоответчики */
    reachedCnt: z.number().int().min(0),
    /** Живые разговоры по меткам: «Дозвон» на пульте считается по ним (31.08.2026) */
    contactCnt: z.number().int().min(0),
    leadsCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    /** Заработок за смену: лиды × ставка pay.per_lead_rub; null — ставка не задана */
    earnedRub: z.number().int().min(0).nullable(),
});
/** Состояние линии: пока линии нет, раздача на диспетчера прекращается (§7.9) */
export const deskLineStateSchema = z.object({
    sipRegistered: z.boolean(),
    ready: z.boolean(),
    /** Карусель гудит за этого диспетчера: пульт показывает «автодозвон» (§7.11) */
    carouselActive: z.boolean(),
    blockedReason: blockedReasonSchema.nullable(),
    /**
     * Цифры, из которых получилась причина простоя.
     *
     * Без них диспетчер читает «Держит открытые карточки» как поломку и идёт к
     * администратору. С ними видно, что делать: «3 карточки из 3 — проставьте
     * метку». Считает их та же функция, что и раздача, — экран ничего не
     * пересчитывает сам.
     */
    openNow: z.number().int().min(0),
    maxOpen: z.number().int().min(0),
    usedToday: z.number().int().min(0),
    dailyLimit: z.number().int().min(0),
});
/**
 * GET /api/desk/snapshot?dispatcherId=…
 *
 * Без параметра — свой пульт. С параметром — чужой: админ и супервизор
 * открывают пульт диспетчера на чтение («смотрю его глазами») и могут ставить
 * метки; звонить с чужого пульта нельзя — линия живёт в браузере владельца.
 */
export const deskSnapshotQuerySchema = z.object({
    dispatcherId: bigIdSchema.optional(),
});
/** GET /api/desk/snapshot */
export const deskSnapshotResponseSchema = z.object({
    /** Номер последнего события журнала на момент снимка: с него пульт продолжает поток */
    seq: z.number().int().min(0),
    serverTime: isoDateTimeSchema,
    line: deskLineStateSchema,
    assignments: z.array(deskAssignmentSchema),
    stats: deskDailyStatsSchema,
});
/**
 * GET /api/desk/next — чтение уже назначенной строки, без выборки из общего пула.
 * Второго механизма выдачи работы не существует (§6.3), пустой ответ объясняется причиной.
 */
export const deskNextResponseSchema = z.object({
    assignment: deskAssignmentSchema.nullable(),
    blockedReason: blockedReasonSchema.nullable(),
});
/**
 * Горизонт перезвона в днях. Дальше — это не «перезвонить», а «закрыть»:
 * элемент очереди с available_from в далёком будущем невидим и для раздачи,
 * и для алертов «НДЗ без движения» — опечатка в годе молча хоронила бы
 * купленный контакт. Проверяют сервер (applyLabel) и пульт (callbackTimeIssue).
 */
export const CALLBACK_MAX_DAYS = 30;
/**
 * GET /api/desk/callbacks?dispatcherId=…
 *
 * Договорённости «позвонить в такое-то время», ещё не выданные карточками:
 * блок «Перезвоны» на пульте и звуковое напоминание в момент dueAt. Выданный
 * перезвон отсюда исчезает — он уже лежит карточкой с плашкой договорённости.
 */
export const deskCallbacksResponseSchema = z.object({
    items: z.array(z.object({
        queueItemId: bigIdSchema,
        phone: phoneE164Schema,
        fullName: z.string().nullable(),
        dueAt: isoDateTimeSchema,
        comment: z.string().nullable(),
        labelTitle: z.string(),
        /** false — время рассчитано кругом, а не назначено человеком: пульт по нему не будит */
        scheduled: z.boolean(),
        /** Время, назначенное человеком (до подгонки под рабочее окно); null у легаси-строк */
        scheduledAt: isoDateTimeSchema.nullable(),
    })),
});
/**
 * POST /api/desk/callbacks/:id/take — диспетчер забирает свой перезвон карточкой
 * по клику в блоке «Перезвоны» (06.09.2026). Это не выдача из общего пула
 * (ADR-0006): элемент уже закреплён за ним и закрыт его же меткой «спросить
 * время». Ответ — та же карточка, что придёт в снапшоте: пульт ставит её
 * активной и набирает, не дожидаясь события.
 */
export const deskCallbackTakeResponseSchema = z.object({
    assignment: deskAssignmentSchema,
});
/** POST /api/desk/assignments/:id/label */
export const deskLabelBodySchema = z.object({
    labelId: refIdSchema,
    comment: z.string().max(2000).optional(),
    /** «Перезвонить в …» — бьёт задержку круга по умолчанию (§5.4). Будущее, не дальше CALLBACK_MAX_DAYS */
    nextActionAt: isoDateTimeSchema.optional(),
});
/** Что стало с контактом после метки: пульт показывает это диспетчеру */
export const deskLabelResponseSchema = z.object({
    assignmentId: bigIdSchema,
    queueStatus: z.enum(['done', 'new', 'robot']),
    nextRound: z.number().int().min(1).max(20).nullable(),
    availableFrom: isoDateTimeSchema.nullable(),
    isLead: z.boolean(),
    /** Повтор того же клика: не ошибка, а текущее состояние (§6.4) */
    idempotent: z.boolean(),
});
/**
 * POST /api/desk/assignments/:id/call-intent — сервер выдаёт заголовки для INVITE.
 * Заголовки формирует браузер, поэтому телефония их всё равно перепроверяет (§7.3).
 */
export const deskCallIntentBodySchema = z.object({
    phone: phoneE164Schema,
});
export const deskCallIntentResponseSchema = z.object({
    callTraceId: z.string().length(26),
    sipHeaders: z.object({
        'X-Assignment-Id': z.string(),
        'X-Call-Trace-Id': z.string(),
    }),
});
/**
 * POST /api/desk/manual-call — набор произвольного номера с пульта, без карточки.
 *
 * Номер приходит как его набрал человек: 8 999…, +7 999…, со скобками и
 * пробелами. Нормализует его сервер той же функцией, что и приём контактов, —
 * второй реализации правила E.164 в системе нет (§6.8). Поэтому здесь строка
 * произвольного вида, а не `phoneE164Schema`: пульт не обязан уметь то, что
 * умеет сервер, а «поправьте формат» — плохой ответ на набранный номер.
 */
export const deskManualCallBodySchema = z.object({
    phone: z.string().min(1).max(32),
});
/**
 * Ответ несёт номер В НОРМАЛИЗОВАННОМ виде: набирать нужно именно его, потому
 * что диалплан принимает только `_+7XXXXXXXXXX` и на '8 999…' отвечает отбоем.
 *
 * `X-Assignment-Id` со значением `MANUAL_CALL_MARKER` — не признак прав, а
 * пометка «карточки нет»: телефония по ней узнаёт служебный звонок и сама
 * перепроверяет и номер, и часовой лимит (§7.3).
 */
export const deskManualCallManualSchema = z.object({
    kind: z.literal('manual'),
    callTraceId: z.string().length(26),
    phone: phoneE164Schema,
    sipHeaders: z.object({
        'X-Assignment-Id': z.string(),
        'X-Call-Trace-Id': z.string(),
    }),
    /** Сколько ручных звонков ещё остаётся в текущем часе: пульт показывает это до набора */
    remainingThisHour: z.number().int().min(0),
});
/**
 * «Свой номер» (24.09.2026): номер не из сегодняшней базы выдан КАРТОЧКОЙ.
 * Пульт кладёт её в снапшот, делает активной и звонит обычным call-intent —
 * тогда звонок привязан к карточке и метка ставится как у выданного номера.
 */
export const deskManualCallCardSchema = z.object({
    kind: z.literal('card'),
    assignmentId: bigIdSchema,
    assignment: deskAssignmentSchema,
    phone: phoneE164Schema,
    /** Сколько своих номеров ещё можно набрать сегодня. */
    ownNumbersLeftToday: z.number().int().min(0),
    ownNumbersPerDay: z.number().int().min(0),
});
export const deskManualCallResponseSchema = z.discriminatedUnion('kind', [
    deskManualCallManualSchema,
    deskManualCallCardSchema,
]);
/**
 * POST /api/desk/request-contacts — «Запросить номера у администратора».
 * Авто-раздача добирает сама, поэтому это сигнал «очередь пуста», а не выдача.
 * duplicate=true — открытый запрос уже висел, админ его и так видит.
 */
export const deskRequestContactsResponseSchema = z.object({ duplicate: z.boolean() });
/** POST /api/desk/ready — кнопка «Готов / Перерыв» */
export const deskReadyBodySchema = z.object({ ready: z.boolean() });
/**
 * Состояние готовности ПОСЛЕ переключения, а не эхо запроса: пульт рисует кнопку
 * по ответу сервера, и своей копии этого флага у браузера нет (§6.2).
 */
export const deskReadyResponseSchema = z.object({ ready: z.boolean() });
/**
 * POST /api/desk/audio-check — итог проверки звука из пульта (§7.9).
 *
 * Диспетчер жмёт «Проверить звук», браузер меряет три вещи и присылает сюда
 * их итог. Аудио НЕ передаётся: запись играется у диспетчера в наушниках и
 * там же остаётся — на сервере она никому не нужна, а голос человека без
 * нужды по сети не гоняют.
 */
export const audioCheckBodySchema = z.object({
    /** Пиковый уровень микрофона, 0-100. Ноль — браузер не услышал ничего */
    micPeakPercent: z.number().int().min(0).max(100),
    /** Адреса локальной сети: они есть всегда, сами по себе ничего не доказывают */
    hostCandidates: z.number().int().min(0).max(100),
    /** Публичный адрес через STUN. Ноль — станции некуда слать звук */
    srflxCandidates: z.number().int().min(0).max(100),
    /** Запасной канал через TURN, если он настроен */
    relayCandidates: z.number().int().min(0).max(100),
    iceElapsedMs: z.number().int().min(0).max(60_000),
    inputDeviceId: z.string().max(200).nullable(),
    outputDeviceId: z.string().max(200).nullable(),
    userAgent: z.string().max(400),
});
//# sourceMappingURL=desk.dto.js.map